import React, { useState, useEffect, useMemo, useRef } from "react";

import { Box, FormControl, InputLabel, FormControlLabel, MenuItem, Select, Typography, Checkbox } from "@mui/material";
import BpRadio from "./BpRadio";

const CmLabel = (props) => {
    const { children, ...lProps } = props;
    return (
        <label style={{ color: '#6D7280', fontSize: '14px' }} {...lProps}>{children}</label>
    );
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const Step2 = ({ extrasGuide = [], extrasVehicle = [], extrasAttractions = [], initialGuide = null, persons = {}, initialVehicle = null, initialAttractions = [], onSelectionChange = (..._args) => { }, modalOpen = false, guideIncluded = undefined, initialVehicleOptionChecked = false }) => {
    // normalize guide options: support array of objects or legacy strings
    const hasGuideExtras = Array.isArray(extrasGuide) && extrasGuide.length > 0;
    const guideOptions = useMemo(() => {
        if (!hasGuideExtras) return [];
        const mapped = extrasGuide.map(g => {
            if (!g) return null;
            if (typeof g === 'string') return { id: String(g), type_guide: g, price: 0 };
            const stableId = g.id ?? g.type_guide ?? g.type ?? g.name ?? String(g.title ?? g.label ?? 'guide');
            return { id: String(stableId), type_guide: g.type_guide ?? g.type ?? g.name ?? String(g.id ?? stableId), price: g.price ?? 0, excepcions: g.excepcions ?? [] };
        }).filter(Boolean);

        // Only return exactly what comes from extrasGuide (no special items)
        return mapped;
    }, [extrasGuide, hasGuideExtras, guideIncluded]);

    // normalize vehicle options
    const hasVehicleExtras = Array.isArray(extrasVehicle) && extrasVehicle.length > 0;
    const vehicleOptions = useMemo(() => {
        if (!hasVehicleExtras) return [];
        const mapped = extrasVehicle.map(v => {
            if (!v) return null;
            if (typeof v === 'string') return { id: String(v), vehicle_type: v, price: 0 };
            // prefer stable id fields; fallback to deterministic stringified name
            const stableId = v.id ?? v.vehicle_type ?? v.name ?? String(v.title ?? v.label ?? 'vehicle');
            return { id: String(stableId), vehicle_type: v.vehicle_type ?? v.name ?? String(v.id ?? stableId), price: v.price ?? 0, vehicle_option: v.vehicle_option ?? null, excepcions: v.excepcions ?? [] };
        }).filter(Boolean);
        // Only return exactly what comes from extrasVehicle (no injected options)
        return mapped;
    }, [extrasVehicle, hasVehicleExtras]);

    const [atraction, setAtraction] = useState('Actraction 1');
    const [vehicleKey, setVehicleKey] = useState(() => {
        // start empty; initialization occurs on modal open or when initialVehicle changes
        return '';
    });
    const [guideKey, setGuideKey] = useState(() => {
        // start empty; initialization occurs on modal open or when initialGuide changes
        return '';
    });
    const [vehicleOptionChecked, setVehicleOptionChecked] = useState(() => Boolean(initialVehicleOptionChecked));
    const getAttractionKey = (a) => {
        if (!a) return String(a);
        if (typeof a === 'string') return a;
        return a.id ?? a.name ?? a.title ?? a.label ?? String(a);
    };

    const [selectedAttractions, setSelectedAttractions] = useState(() => {
        if (!Array.isArray(initialAttractions)) return [];
        return initialAttractions.map(a => getAttractionKey(a));
    });

    // attractions init ref to avoid re-initializing while user interacts
    const attractionsInitRef = useRef(false);

    // refs to avoid re-initializing repeatedly
    const initRef = useRef(false);

    const handleChangeAtraction = (event) => {
        setAtraction(event.target.value);
    }

    const handleChangeVehicle = (event) => {
        setVehicleKey(event.target.value);
    }

    const handleChangeGuide = (event) => {
        const value = event.target.value;
        setGuideKey(value);
    }

    const handleToggleVehicleOption = () => {
        setVehicleOptionChecked(prev => !prev);
    }

    // When modal opens or when initialGuide changes, initialize guideKey once
    useEffect(() => {
        // initialize when modal opens or when initialGuide changes; allow re-init on each modal open
        if (!modalOpen) return;
        // if guide explicitly not included, ensure empty and skip
        if (Number(guideIncluded) === 0) {
            setGuideKey('');
            initRef.current = true;
            return;
        }
        // prefer initialGuide if provided
        if (initialGuide && (typeof initialGuide === 'string' || initialGuide.id || initialGuide.type_guide)) {
            const id = typeof initialGuide === 'string' ? initialGuide : (initialGuide.id ?? initialGuide.type_guide);
            setGuideKey(String(id));
            initRef.current = true;
            return;
        }
        // if no guide extras, keep empty
        if (!hasGuideExtras) {
            setGuideKey('');
            initRef.current = true;
            return;
        }
        // otherwise pick the first available guide option (prefer price===0)
        const freeGuide = guideOptions.find(g => Number(g.price) === 0) || guideOptions[0];
        setGuideKey(String(freeGuide?.id ?? ''));
        initRef.current = true;
    }, [modalOpen, initialGuide, guideOptions, hasGuideExtras, guideIncluded]);

    // vehicle init ref
    const vehicleInitRef = useRef(false);

    // initialize vehicleKey on modal open or when initialVehicle changes
    useEffect(() => {
        if (!modalOpen) return;
        // if the only vehicle option is id === 0, clear selection and skip
        if (vehicleOptions.length === 1 && String(vehicleOptions[0].id) === '0') {
            setVehicleKey('');
            vehicleInitRef.current = true;
            return;
        }
        // prefer initialVehicle if provided
        if (initialVehicle && (typeof initialVehicle === 'string' || initialVehicle.id || initialVehicle.vehicle_type)) {
            const id = typeof initialVehicle === 'string' ? initialVehicle : (initialVehicle.id ?? initialVehicle.vehicle_type);
            setVehicleKey(String(id));
            vehicleInitRef.current = true;
            return;
        }
        if (!hasVehicleExtras) {
            setVehicleKey('');
            vehicleInitRef.current = true;
            return;
        }
        // prefer free option then first
        const freeOption = vehicleOptions.find(v => Number(v.price) === 0) || vehicleOptions[0];
        if (freeOption) {
            setVehicleKey(String(freeOption.id));
            vehicleInitRef.current = true;
        }
    }, [modalOpen, initialVehicle, vehicleOptions, hasVehicleExtras]);

    // initialize vehicleOptionChecked when vehicle changes (use vehicle_option.value if provided)
    useEffect(() => {
        const selectedVehicle = vehicleOptions.find(v => v.id === vehicleKey) || null;
        if (!selectedVehicle || !('vehicle_option' in selectedVehicle) || !selectedVehicle.vehicle_option) {
            // If parent provided an explicit initial flag, prefer it when no vehicle_option exists
            setVehicleOptionChecked(Boolean(initialVehicleOptionChecked && (!selectedVehicle || !selectedVehicle.vehicle_option)) || false);
            return;
        }
        const vo = selectedVehicle['vehicle_option'];
        const first = Array.isArray(vo) ? vo[0] : vo;
        const val = first?.value ?? 0;
        // if modal just opened and parent passed an explicit flag, prefer parent's value
        if (modalOpen && typeof initialVehicleOptionChecked !== 'undefined') {
            setVehicleOptionChecked(Boolean(initialVehicleOptionChecked));
        } else {
            setVehicleOptionChecked(Number(val) > 0);
        }
    }, [vehicleKey, vehicleOptions]);

    // Notify parent when guide or vehicle selection changes; provide full selected objects
    useEffect(() => {
        const selectedGuide = guideOptions.find(g => g.id === guideKey) || null;
        const selectedVehicle = vehicleOptions.find(v => v.id === vehicleKey) || null;
        // map selected attractions to full objects
        const selectedAttractionsObjs = (Array.isArray(extrasAttractions) ? extrasAttractions : [])
            .filter(a => selectedAttractions.includes(getAttractionKey(a)))
            .map(a => ({ ...a }));

        let vehicleWithOption = null;
        if (selectedVehicle) {
            vehicleWithOption = { ...selectedVehicle };
            // coerce to any for safe property access in JS file to satisfy static checks
            /** @type {any} */
            const selAny = selectedVehicle;
            const voVal = selAny && selAny.vehicle_option;
            if (voVal) {
                const voList = Array.isArray(voVal) ? voVal : [voVal];
                const firstVo = voList[0] || null;
                /** @type {any} */
                const vwoAny = vehicleWithOption;
                if (vehicleOptionChecked && firstVo) {
                    // ensure vehicle_option contains a numeric value property
                    vwoAny.vehicle_option = { ...firstVo, value: Number(firstVo?.value ?? 1) || 1 };
                } else if (firstVo) {
                    // not checked -> set value 0
                    vwoAny.vehicle_option = { ...firstVo, value: 0 };
                }
            }
        }

        onSelectionChange({ guide: selectedGuide, vehicle: vehicleWithOption, attractions: selectedAttractionsObjs, vehicleOptionChecked });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guideKey, guideOptions, vehicleKey, vehicleOptions, vehicleOptionChecked, selectedAttractions]);

    // initialize selectedAttractions when modal opens or when initialAttractions change
    useEffect(() => {
        if (!modalOpen && attractionsInitRef.current) return;
        if (Array.isArray(initialAttractions) && initialAttractions.length > 0) {
            setSelectedAttractions(initialAttractions.map(a => getAttractionKey(a)));
            attractionsInitRef.current = true;
            return;
        }
        // if there are no attractions extras, ensure empty
        if (!Array.isArray(extrasAttractions) || extrasAttractions.length === 0) {
            setSelectedAttractions([]);
            attractionsInitRef.current = true;
            return;
        }
        // otherwise leave current selection (do not override user's choice)
    }, [modalOpen, initialAttractions, extrasAttractions]);

    const toggleAttraction = (name) => {
        setSelectedAttractions(prev => {
            const exists = prev.includes(name);
            return exists ? prev.filter(n => n !== name) : [...prev, name];
        });
    }

    return (
        <>
            <Typography
                variant="h5"
                sx={{
                    fontSize: '20px',
                    color: 'secondary.main'
                }}
            >
                Detalles del tour
            </Typography>
            {Number(guideIncluded) !== 0 && guideOptions && guideOptions.length > 0 && (
                <Box
                    sx={{
                        display: 'grid',
                        width: '100%',
                        py: '5px'
                    }}
                >
                    <CmLabel htmlFor="guideType">¿Como quieres hacer tu tour?</CmLabel>
                    <Select
                        labelId="guide-type"
                        name="guideType"
                        value={guideKey}
                        onChange={handleChangeGuide}
                        disabled={!hasGuideExtras}
                        MenuProps={MenuProps}
                        sx={{
                            fontSize: '13px',
                            height: '40px',
                            borderRadius: '8px'
                        }}
                    >
                        {guideOptions.map((item) => (
                            <MenuItem
                                key={item.id}
                                value={item.id}
                            >
                                {item.type_guide} {item.price ? `- $${item.price}` : ''}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            )}

            {/* Effect: initialize from initialGuide when modal opens (or on first mount) */}

            {/* other UI below remains unchanged */}

            {(vehicleOptions.length > 1 && String(vehicleOptions[0].id) === '0') && (
                <Box
                    sx={{
                        display: 'grid',
                        width: '100%',
                        py: '5px'
                    }}
                >
                    <CmLabel htmlFor="vehicle">Vehículo</CmLabel>
                    <Select
                        labelId="vehicle-id"
                        name="vehicle"
                        value={vehicleKey}
                        onChange={handleChangeVehicle}
                        disabled={!hasVehicleExtras}
                        MenuProps={MenuProps}
                        sx={{
                            fontSize: '13px',
                            height: '40px',
                            borderRadius: '8px'
                        }}
                    >
                        {vehicleOptions.map((item) => (
                            <MenuItem
                                key={item.id}
                                value={item.id}
                            >
                                {item.vehicle_type} {item.price ? `- $${item.price}` : ''}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            )}

            {(() => {
                const selectedVehicle = vehicleOptions.find(v => v.id === vehicleKey) || null;
                // 1) Condiciones base
                const baseOk =
                    vehicleOptions.length > 1 &&
                    String(vehicleOptions[0]?.id) === '0' &&
                    selectedVehicle != null;

                if (!baseOk) return null;

                // 2) Resolver el vehículo seleccionado desde la lista (según tu vehicleKey)
                const selectedFromList =
                    vehicleOptions.find(v => String(v.id) === String(vehicleKey)) || null;

                if (!selectedFromList || !('vehicle_option' in selectedFromList) || !selectedFromList.vehicle_option) {
                    return null;
                }

                // 3) Tomar la opción (acepta arreglo u objeto)
                const vo = Array.isArray(selectedFromList.vehicle_option)
                    ? selectedFromList.vehicle_option[0]
                    : selectedFromList.vehicle_option;

                if (!vo) return null;

                // 4) Construir label
                const label = `${vo?.name ?? 'Opción'}${vo?.price ? ` - $${vo.price}` : ''}`;

                // 5) Renderizar TODO el bloque (incluido CmLabel) solo si todo lo anterior es válido
                return (
                    <Box sx={{ pb: '10px' }}>
                        <Box>
                            <CmLabel htmlFor="complements">Complementos</CmLabel>
                        </Box>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={vehicleOptionChecked}
                                    onChange={handleToggleVehicleOption}
                                />
                            }
                            label={label}
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                        />
                    </Box>
                );
            })()}



            <Box
                sx={{
                    display: 'grid',
                    width: '100%',
                    py: '5px'
                }}
            >
                <CmLabel htmlFor="atractions">Atracciones del tour</CmLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    {(Array.isArray(extrasAttractions) ? extrasAttractions : []).map((item) => {
                        const name = item.name || 'Sin nombre';
                        const price = (item.rates && item.rates[0] && item.rates[0].price) || 0;
                        const svg = item.icon || null;
                        const itemKey = getAttractionKey(item);
                        const checked = selectedAttractions.includes(itemKey);
                        const paxCount = Object.values(persons).reduce((s, v) => s + (Number(v) || 0), 0) || 1;
                        return (
                            <Box key={itemKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1, border: '1px solid #eee' }}>
                                <Box sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }} dangerouslySetInnerHTML={svg ? { __html: svg } : undefined} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: 13, color: 'secondary.main' }}>{name}</Typography>
                                    <Typography sx={{ fontSize: 12, color: 'secondary.main' }}>{price ? `$${price} x ${paxCount}` : 'Gratis'}</Typography>
                                </Box>
                                <Checkbox checked={checked} onChange={() => toggleAttraction(itemKey)} />
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        </>
    )
}

export default Step2;